import { useEffect, useState, useCallback } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useDebounced } from '../hooks/useDebounced.js';
import { highlight, snippetAround } from '../util/highlight.jsx';

// T358 — the mail belonging to one candidature, readable here instead of in
// Gmail.
//
// Until now `Stage history` was entirely hand-made: every row is a move the
// operator performed, and `respondedAtSource` says `inferred` because the reply
// date is deduced from when he dragged the card. The mail that prompted the
// move existed, in Gmail, unseen. This is that mail.
//
// It reports only. Nothing here advances a stage — linking a message says
// "this belongs to that application", not "that application moved".

function fmt(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const METHOD_LABEL = {
  thread: 'matched by reply thread',
  'ats-tenant': 'matched by the recruiting platform',
  'company-domain': 'matched by sender domain',
  'company-text': 'matched by company name in the message',
  manual: 'linked by you',
  // T367 — said plainly. A link a model made is not the same claim as one a
  // threading header made, and he should be able to tell at a glance.
  ai: 'matched by Claude reading the message',
  // T389 — these four existed as match methods and had no label, so the line
  // rendered the raw slug (`role-title`) in a row of full sentences. Two of
  // them predate this change.
  'applied-date': 'matched by company and apply date',
  'role-title': 'matched by the role quoted in the message',
  'board-job-id': 'matched by the job board\u2019s own job id',
  'board-confirmation': 'matched by the company and role in the confirmation',
  // T485/T486 — the methods that were missing, including the two CORRECTIONS.
  // `-live` is any method that T446 then moved off a closed row onto the live
  // twin of the same posting; `ats-tenant-named` is T479 moving onto the live
  // row whose title the subject spells out. Without these the line rendered
  // the raw slug in a row of full sentences, which is the same defect T389
  // fixed for four other methods.
  'role-at-named-company': 'matched by the role in the subject at a company the message names',
  'ats-tenant-named': 'matched by the recruiting platform, on the open role the subject names',
};

// T486 — a correction suffix rather than a method of its own, so the label is
// derived the same way the method is (Email.js). Written out by hand, this
// table fell four behind.
for (const base of Object.keys(METHOD_LABEL)) {
  if (base.endsWith('-live') || base === 'ats-tenant-named') continue;
  METHOD_LABEL[`${base}-live`] = `${METHOD_LABEL[base]}, then moved to the open duplicate`;
}

function Message({ email, onDeleted, q = '' }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(false);

  // The list omits bodyText on purpose — a thread of forty full messages is
  // megabytes the panel never shows. Fetch one when it is actually opened.
  const expand = async () => {
    if (open) return setOpen(false);
    setOpen(true);
    if (body !== null) return;
    setLoading(true);
    try {
      const r = await api.get(`/emails/${email._id}`);
      setBody(r.email?.bodyText ?? '');
    } catch (e) {
      setBody('');
      toast(e.body?.message || 'Could not load that message.', { kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <li className="rounded-md border border-slate-800 bg-slate-900/40">
      <button
        type="button"
        onClick={expand}
        aria-expanded={open}
        className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-slate-800/60"
      >
        <span aria-hidden="true" className="mt-0.5 text-slate-500">
          {open ? '▾' : '▸'}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-slate-200">
            {q ? highlight(email.subject || '(no subject)', q) : email.subject || '(no subject)'}
          </span>
          <span className="mt-0.5 block truncate text-[11px] text-slate-500">
            {email.fromName ? `${email.fromName} · ` : ''}
            {email.from}
          </span>
          {/* T362 — a hit in the body is invisible from the subject line, so a
              search result shows the sentence the word is actually in. */}
          {q && email.bodyText && (
            <span className="mt-1 block text-[11px] leading-relaxed text-slate-400">
              {highlight(snippetAround(email.bodyText, q), q)}
            </span>
          )}
        </span>
        <span className="shrink-0 text-[11px] tabular-nums text-slate-500">{fmt(email.sentAt)}</span>
      </button>
      {open && (
        <div className="border-t border-slate-800 px-3 py-2">
          {loading ? (
            <p className="text-[11px] text-slate-500">loading…</p>
          ) : (
            <>
              {/* Plain text, rendered as plain text. Nothing stored is markup,
                  and `whitespace-pre-wrap` keeps the sender's line breaks
                  without letting anything they wrote become an element. */}
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words font-sans text-[11px] leading-relaxed text-slate-300">
                {q ? highlight(body || '(empty)', q) : body || '(empty)'}
              </pre>
              {email.bodyTruncated && (
                <p className="mt-1 text-[10px] text-slate-500">
                  Truncated — the rest is in your mailbox.
                </p>
              )}
              <div className="mt-2 flex items-center gap-3 text-[10px]">
                {email.matchMethod && <span className="text-slate-500">{METHOD_LABEL[email.matchMethod] ?? email.matchMethod}</span>}
                <button
                  type="button"
                  onClick={() => onDeleted(email._id)}
                  className="text-slate-500 underline decoration-dotted transition hover:text-red-400"
                >
                  Delete this copy
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export default function MailThread({ recommendationId }) {
  const [emails, setEmails] = useState(null);
  const [query, setQuery] = useState('');
  // Debounced because this asks the server, unlike every other search box in
  // the app — see hooks/useDebounced.js.
  const q = useDebounced(query, 250);
  // How many there are with no search on, so the box can say what it filtered
  // down FROM. Without it, "2 messages" after a search is indistinguishable
  // from a thread that only ever had two.
  const [total, setTotal] = useState(null);

  const load = useCallback(async () => {
    if (!recommendationId) return;
    try {
      const params = new URLSearchParams({ recommendationId });
      if (q) params.set('q', q);
      const r = await api.get(`/emails?${params}`);
      setEmails(r.emails ?? []);
      if (!q) setTotal(r.emails?.length ?? 0);
    } catch {
      // A panel that cannot load its mail should say "none yet" rather than
      // break the application view around it.
      setEmails([]);
    }
  }, [recommendationId, q]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/emails/${id}`);
      setEmails((prev) => (prev ?? []).filter((e) => e._id !== id));
      toast('Copy deleted. The mail is still in your mailbox.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not delete that copy.', { kind: 'error' });
    }
  };

  if (emails === null) return null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-500">
          Mail
          {q ? ` (${emails.length} of ${total ?? '?'})` : total ? ` (${total})` : ''}
        </label>
        {/* Shown once there is more than one message — a search box over a
            single message is furniture. */}
        {(total ?? 0) > 1 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this thread…"
            aria-label="Search this thread"
            className="min-w-[160px] flex-1 rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1 text-[11px] text-slate-100 focus:border-brand-500 focus:outline-none sm:max-w-[220px] sm:flex-none"
          />
        )}
      </div>
      {emails.length === 0 ? (
        <p className="mt-2 text-[11px] text-slate-500">
          {q
            ? `Nothing in this thread matches “${q}”.`
            : 'No mail linked yet. The scan reads replies from recruiting platforms and from companies you have applied to.'}
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {emails.map((e) => (
            <Message key={e._id} email={e} onDeleted={remove} q={q} />
          ))}
        </ul>
      )}
    </div>
  );
}
